import { CheckIcon } from "@heroicons/react/outline";
import * as React from "react";
import FeatureItemLeftSide from "../../templates/featureItemLeft";
import AutomationReportsImage from "./../../images/features-automation/automation-reports.png";
import DragDropEditorImage from "./../../images/features-automation/drag-drop-editor.png";
import MultipleChannelsImage from "./../../images/features-automation/multiple-channels.png";
import CallToAction from "./../../templates/callToAction";
import FeaturesHeroSection from "./../../templates/featuresHeroSection";
import FooterSection from "./../../templates/footer";
import NavBar from "./../../templates/navbar";
import TestimonialsSection from "./../../templates/testomonials";
import { Helmet } from "react-helmet";

// Import Icons
import CartAbandonmentIcon from "./../../images/icons/cart-abandonment.svg";
import CrossSellIcon from "./../../images/icons/cross-sell.svg";
import OrderConfirmationIcon from "./../../images/icons/order-confirmation.svg";
import ProductAbandonmentIcon from "./../../images/icons/product-abandonment.svg";
import ShippingConfirmationIcon from "./../../images/icons/shipping-confirmation.svg";
import WelcomeSeriesIcon from "./../../images/icons/welcome-series.svg";

const info = {
  multiChannelFeatures: [
    {
      title: "Email, SMS, WebPush",
    },
    {
      title: "WhatsApp",
    },
    {
      title: "Facebook & Instagram",
    },
    {
      title: "Google & Youtube Ads",
    },
  ],
  templatesList: [
    {
      title: "Cart Abandonment",
      description:
        "Recover abandoned carts with pre-built workflows with timely emails, WhatsApp, SMS, and social media ads.",
      icon: CartAbandonmentIcon,
    },
    {
      title: "Product Abandonment",
      description:
        "Reach-out to visitors who are just browsing products and not making any purchases.",
      icon: ProductAbandonmentIcon,
    },
    {
      title: "Welcome Series",
      description:
        "Encourage visitors to become a customers. Send them personalized messages welcoming them to your brand!",
      icon: WelcomeSeriesIcon,
    },
    {
      title: "Order Confirmations",
      description:
        "Send an order confirmation and COD confirmation messages over WhatsApp, SMS or an emails right after they make a purchase.",
      icon: OrderConfirmationIcon,
    },
    {
      title: "Shipping Confirmations",
      description:
        "Send shipping confirmation and delay messages via SMS, Emails, Whatsapp and other channels.",
      icon: ShippingConfirmationIcon,
    },
    {
      title: "Cross-sell Products",
      description: `Suggest more recommended products based on your customer's purchase history and product interests.`,
      icon: CrossSellIcon,
    },
  ],
  segmentFeatures: [
    {
      title: "Drag & Drop Automation Builder",
    },
    {
      title: "Pre-built E-commerce Workflows",
    },
    {
      title: "6+ messaging channels",
    },
    {
      title: "Personalised messages",
    },
  ],
  automationFeaturesList: [
    {
      title: "Cross-Channel Journeys",
      description:
        "Reach-out your customers on 6+ channels in a single journey. Engage your users on a channel they are reachable on.",
    },
    {
      title: "Pre-built Journey Templates",
      description:
        "Use pre-built journeys which are industry standard. Get your journeys up and running within minute",
    },
    {
      title: "Performance Reports",
      description:
        "See how your users are moving in a journey. Identify drop-offs to optimize the journeys further.",
    },
    {
      title: "Personalized Campaigns",
      description:
        "Send personalized and contextual messages using customer's past behaviour and persona.",
    },
    {
      title: "Behaviour Based Flows",
      description:
        "Trigger workflows based on behaviours like not making purchases in a month or interested in a category etc.",
    },
    {
      title: "Act On Campaign Response",
      description:
        "Trigger workflows based on customer response on campaigns like when email opened, link clicked etc.",
    },
  ],
  automationREportFeatures: [
    {
      title: "Analyse customer entering and leaving journeys",
    },
    {
      title: "Analyse revenue generated by each journey message",
    },
    {
      title: "Analyse drop-offs at each step of journey",
    },
    {
      title: "Detailed reports of each journey message sent",
    },
  ],
};
// markup
const IndexPage = () => {
  return (
    <main>
      <Helmet>
        <meta charset="utf-8" />
        <meta
          name="description"
          content="Visually build and deliver cross-channel messages based on your customer’s persona and past behaviour. Automate it all with easy to use drag drop journey designer."
        />
        <meta name="google" content="nositelinkssearchbox" />
        <title>Growlytics Marketing Automation</title>
        <link rel="canonical" href="/" />
      </Helmet>

      <NavBar></NavBar>

      {/* Hero Section */}
      <FeaturesHeroSection
        image={DragDropEditorImage}
        title1="Drive More Revenue"
        title2="With Automation"
        description="Visually build and deliver cross-channel messages based on your customer’s persona and past behaviour. Automate it all with easy to use drag drop journey designer."
        paddingRight="pr-0 md:pr-10"
      ></FeaturesHeroSection>

      {/* Multiple Channels Section */}
      <FeatureItemLeftSide
        features={info.multiChannelFeatures}
        image={MultipleChannelsImage}
        badge="Multiple Marketing Channels"
        title="Send messages on multiple channels."
        description="Send personalised and contextual messages via Email, SMS, WhatsApp, Push Notifications & many other channels."
        button={{
          text: "Explore Marketing Channels",
          href: "/channels",
        }}
        imageStyle="pr-2 md:pr-0 pb-6 md:pb-0"
      ></FeatureItemLeftSide>

      {/* Automation Templates */}
      <div className="overflow-hidden bg-gray-50">
        <div className="relative max-w-7xl mx-auto py-12 px-4 sm:px-6 lg:px-8">
          <svg
            className="absolute top-0 left-full transform -translate-x-1/2 -translate-y-3/4 lg:left-auto lg:right-full lg:translate-x-2/3 lg:translate-y-1/4"
            width={404}
            height={784}
            fill="none"
            viewBox="0 0 404 784"
            aria-hidden="true"
          >
            <defs>
              <pattern
                id="8b1b5f72-e944-4457-af67-0c6d15a99f38"
                x={0}
                y={0}
                width={20}
                height={20}
                patternUnits="userSpaceOnUse"
              >
                <rect
                  x={0}
                  y={0}
                  width={4}
                  height={4}
                  className="text-gray-200"
                  fill="currentColor"
                />
              </pattern>
            </defs>
            <rect
              width={404}
              height={784}
              fill="url(#8b1b5f72-e944-4457-af67-0c6d15a99f38)"
            />
          </svg>

          <div className="relative md:pt-10 lg:grid lg:grid-cols-3 lg:gap-x-8 text-center md:text-left">
            <div className="lg:col-span-1">
              <h2 className="text-3xl font-extrabold tracking-tight text-gray-900 sm:text-4xl">
                Pre-built workflow templates
              </h2>
              <p className="mt-4 text-lg text-gray-500">
                Use pre-made automations built for e-commerce to get started
                quickly.
              </p>
            </div>
            <dl className="mt-10 space-y-10 sm:space-y-0 sm:grid sm:grid-cols-2 sm:gap-x-8 sm:gap-y-10 lg:mt-0 lg:col-span-2">
              {info.templatesList.map((feature) => (
                <div key={feature.title}>
                  <dt>
                    <div className="flex items-center justify-center align-items-center h-12 w-12 rounded-md bg-primary-600 text-white text-center m-auto md:ml-0 ">
                      {/* <feature.icon className="h-6 w-6" aria-hidden="true" /> */}
                      <img src={feature.icon} className="h-10" />
                    </div>
                    <p className="mt-5 text-lg leading-6 font-medium text-gray-900">
                      {feature.title}
                    </p>
                  </dt>
                  <dd className="mt-2 text-base text-gray-500">
                    {feature.description}
                  </dd>
                </div>
              ))}
            </dl>
          </div>
        </div>
      </div>

      {/* Reporting Section */}
      <FeatureItemLeftSide
        features={info.automationREportFeatures}
        image={AutomationReportsImage}
        badge="Analytics & Reports"
        title="Advanced automation reports"
        description="Measure performance and revenue generted by the workflow. See
        live view how user moving along in different steps of
        workflow. Identify the dropouts and revenue loss and optimize
        workflows further. "
        imageStyle="px-2 md:px-0 pb-8 md:pb-12"
      ></FeatureItemLeftSide>

      {/* Enterprise Features */}
      <div className="bg-gray-50">
        <div className="max-w-7xl mx-auto py-16 px-4 sm:px-6 lg:py-24 lg:px-8 lg:grid lg:grid-cols-3 lg:gap-x-8">
          <div>
            <h2 className="text-xs md:text-base font-semibold text-primary-600 uppercase tracking-wide sm:text-center md:text-left">
              Build Complex Automations And Create
            </h2>
            <p className="mt-2 text-2xl md:text-3xl font-extrabold text-gray-900 sm:text-center md:text-left">
              Personalized Experiences
            </p>
            <p className="md:block mt-4 text-md md:text-lg text-gray-500 sm:text-center md:text-left">
              Build personalized experience by analyzing customer's interests
              and pro-actively send effective messages on different makreting
              channels.
            </p>
          </div>
          <div className="mt-12 lg:mt-0 lg:col-span-2">
            <dl className="space-y-10 sm:space-y-0 sm:grid sm:grid-cols-2 sm:grid-rows-3 sm:grid-flow-col sm:gap-x-6 sm:gap-y-10 lg:gap-x-8">
              {info.automationFeaturesList.map((feature) => (
                <div key={feature.title} className="relative">
                  <dt>
                    <CheckIcon
                      className="absolute h-6 w-6 text-green-500"
                      aria-hidden="true"
                    />
                    <p className="ml-9 text-lg leading-6 font-medium text-gray-900">
                      {feature.title}
                    </p>
                  </dt>
                  <dd className="mt-2 ml-9 text-base text-gray-500">
                    {feature.description}
                  </dd>
                </div>
              ))}
            </dl>
          </div>
        </div>
      </div>

      {/* Testimonials Section */}
      <TestimonialsSection></TestimonialsSection>

      {/* Call To Action */}
      <CallToAction></CallToAction>

      {/* Add Footer */}
      <FooterSection></FooterSection>
    </main>
  );
};

export default IndexPage;
